import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { useToaster } from 'app/shared/composables/use-toaster';
import { UISocketDataMessage } from 'app/shared/models/UISocket';
import { SocketAction, SocketActionHandler } from 'app/shared/services/SocketActionHandler';

import { NotificationContent, NotificationId } from '../models/notification';
import { notificationMessageKey, toView } from '../presentation/notification-mapper';
import { NotificationResource } from '../resource/notification-resource';

export type NewNotificationContent = { id: NotificationId } & NotificationContent;

export interface UISocketNewNotificationMessage extends UISocketDataMessage {
    notification: NewNotificationContent;
}

@Injectable()
export class NewNotificationHandler implements SocketActionHandler {
    readonly action: SocketAction = 'newInstallerNotification';

    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly toaster = useToaster();
    private readonly notifications = inject(NotificationResource);

    handle(event: UISocketNewNotificationMessage): void {
        this.notifications.reload();
        this.toaster.info({
            title: this.translate.instant('INSTALLER_NEW_MESSAGE_RECEIVED_TITLE'),
            message: this.toastMessage(event.notification),
            onClick: () => this.router.navigate(['/notifications']),
            duration: 3000,
        });
    }

    // Same text as the notification center item, flattened: the toaster renders plain text.
    private toastMessage(content: NotificationContent): string {
        const view = toView(content);
        if (!view) return '';

        const params = view.params ?? {};
        const key = notificationMessageKey(view.type, params);
        const html = view.message || (key ? this.translate.instant(key, params) : '');
        return new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';
    }
}
